import React from "react";
import { Message } from "semantic-ui-react";
import { connect } from "react-redux";

import Notes from "../note/notes";
import Board from "../board/board";
import BlockFundSummary from "./segments/block-fund-summary";
import EstateOwnerSummary from "../estate/owner-summary";

import { FileBox } from "../file/filebox";
import { BlockInfo } from "./segments/info";
import { RoleRequired } from "../auth/role-required";
import { DetailsSummary } from "./segments/details-summary";
import { BillingReports } from "./segments/billing-reports-list";
import { t } from "../../constant/translations";

import { SurveyList } from "./voting";
import { GlobalSettingsCheck } from "../common/global-settings-check";

import styled from "styled-components";
import { device } from "../../config";
import { permissions } from "../../constant/permissions";
import { reverse, routes } from "../../routes";
import { bindActionCreators } from "redux";
import { deleteBlock } from "../../redux/modules/blocks";
import { DeleteModal } from "../../components/common/delete-modal";
import { Hero } from "./hero/hero";

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;

  @media ${device.desktop} {
    flex-direction: row;
    height: 100vh;
    overflow: hidden;
  }
`;

const Sidebar = styled.div`
  background: #fafaf9;
  border-bottom: 1px solid #e7e5e4;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  order: 2;

  @media ${device.desktop} {
    width: 280px;
    border-bottom: none;
    border-right: 1px solid #e7e5e4;
    overflow-y: auto;
    overflow-x: hidden;
    order: 0;
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
`;

const RightCol = styled.div`
  display: contents;
  order: 0;

  @media ${device.desktop} {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    background: #fff;
  }
`;

const HeroSlot = styled.div`
  order: 1;
`;

const ContentSlot = styled.div`
  order: 3;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
`;

const SidebarSection = styled.div`
  padding: 12px 16px;
`;

const SidebarLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a8a29e;
  margin-bottom: 8px;
`;

const TabContent = styled.div`
  flex: 1;
  padding: 16px 20px;
`;

const Divider = styled.div`
  border-top: 1px solid #e7e5e4;
  margin-top: 28px;
`;

const ContentSection = styled.div`
  margin-top: ${({ noBorder }) => (noBorder ? "0" : "28px")};
`;

const ArchivedBanner = styled.div`
  padding: 12px 16px;
  flex-shrink: 0;
`;

function BlockDetails(props) {
  return (
    <PageWrap>
      <Sidebar>
        <SidebarSection>
          <DetailsSummary
            block={props.block}
            history={props.history}
            estates={props.estates}
          />
        </SidebarSection>

        <RoleRequired staff={true} permission={permissions.notesView}>
          <SidebarSection>
            <SidebarLabel>{t("block.notes")}</SidebarLabel>
            <Notes entity={props.block} entityType="block" />
          </SidebarSection>
        </RoleRequired>
      </Sidebar>

      <RightCol>
        <HeroSlot>
          <Hero block={props.block} />
        </HeroSlot>
        <ContentSlot>
          {!props.block.active && (
            <ArchivedBanner>
              <Message error icon={"trash"}>
                <Message.Header>{t("block.object_archived")}</Message.Header>
                <Message.Content>
                  <DeleteModal
                    entity={props.block}
                    del={props.del}
                    floated={"right"}
                  />
                </Message.Content>
              </Message>
            </ArchivedBanner>
          )}
          <TabContent>
            <RoleRequired userOnly={true}>
              <ContentSection noBorder>
                <EstateOwnerSummary block={props.block} />
              </ContentSection>
              <ContentSection>
                <SidebarLabel>{t("billing.reports")}</SidebarLabel>
                <BillingReports block={props.block} />
              </ContentSection>
              <Divider />
            </RoleRequired>
            <ContentSection>
              <Board block={props.block} />
            </ContentSection>
            <GlobalSettingsCheck check={(s) => s.features?.survey}>
              <SurveyList block={props.block} history={props.history} />
            </GlobalSettingsCheck>
            <ContentSection>
              <SidebarLabel>{t("block.info")}</SidebarLabel>
              <BlockInfo block={props.block} />
            </ContentSection>
            {props.canSeeFunds && (
              <ContentSection>
                <SidebarLabel>{t("billing.tills")}</SidebarLabel>
                <BlockFundSummary block={props.block} />
              </ContentSection>
            )}
            <ContentSection>
              <SidebarLabel>{t("block.documents")}</SidebarLabel>
              <FileBox block={props.block} />
            </ContentSection>
          </TabContent>
        </ContentSlot>
      </RightCol>
    </PageWrap>
  );
}

const mapStateToProps = (state) => {
  const block = state.blocks.selected;
  return {
    block: block,
    estates: state.estates.entities,
    userRole: state.auth.user?.role,
    canSeeFunds: state.auth.user?.hasPermission(
      permissions.billingAccountViewBalance
    ),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const del = (data) => (dispatch) => {
    dispatch(deleteBlock(data)).then(() => {
      ownProps.history.push(reverse(routes.blockList));
    });
  };
  return bindActionCreators({ del }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(BlockDetails);
